import React, { useEffect, useRef, useState } from "react";
import { Badge, Button } from "react-bootstrap";

import { countRecognizedFields, getAcceptedExtensions, getAnalysisWarning, hasReadableData } from "./document-intake-state";

const FALLBACK_ACCEPT = ".pdf,.jpg,.jpeg,.png";
const ANALYZING_MESSAGES = [
  "Leyendo el documento…",
  "Identificando los campos…",
  "Verificando que sea auténtico…",
  "Preparando tu solicitud…",
];
const MESSAGE_INTERVAL_MS = 2600;

/** Mensaje que va cambiando mientras la IA trabaja, para que la espera se sienta activa. */
const useRotatingMessage = (active) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (!active) {
      setIndex(0);
      return undefined;
    }
    const timer = setInterval(
      () => setIndex((current) => Math.min(current + 1, ANALYZING_MESSAGES.length - 1)),
      MESSAGE_INTERVAL_MS
    );
    return () => clearInterval(timer);
  }, [active]);
  return ANALYZING_MESSAGES[index];
};

const ScanAnimation = ({ entry }) => (
  <div className="di-scan" aria-hidden="true">
    {entry.previewUrl ? (
      <img className="di-scan-preview" src={entry.previewUrl} alt="" />
    ) : (
      <i className="fa fa-file-alt di-scan-icon" />
    )}
    <div className="di-scan-line" />
  </div>
);

/**
 * Un documento solicitado: zona para arrastrar o elegir archivo y, segun el estado, el avance
 * de subida, el escaner mientras la IA analiza, o el resultado. `entry.phase` es
 * "uploading" | "analyzing" | "done" | "error"; sin entry, la zona esta libre.
 */
const DocumentSlot = ({ requirement, type, entry, onFile }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const busy = !!entry && (entry.phase === "uploading" || entry.phase === "analyzing");
  const message = useRotatingMessage(!!entry && entry.phase === "analyzing");
  const accept = type ? getAcceptedExtensions(type.mimeTypes).join(",") || FALLBACK_ACCEPT : FALLBACK_ACCEPT;

  const openPicker = () => {
    if (!busy && inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (!busy && event.dataTransfer.files.length > 0) {
      onFile(event.dataTransfer.files[0]);
    }
  };

  const header = (
    <div className="d-flex align-items-center justify-content-between mb-2">
      <span className="font-weight-bold">{requirement.label}</span>
      <Badge variant={requirement.required ? "danger" : "secondary"}>
        {requirement.required ? "Requerido" : "Opcional"}
      </Badge>
    </div>
  );

  const hiddenInput = (
    <input
      ref={inputRef}
      type="file"
      hidden
      accept={accept}
      onChange={(event) => {
        onFile(event.target.files[0]);
        event.target.value = "";
      }}
    />
  );

  if (busy) {
    const uploading = entry.phase === "uploading";
    return (
      <div className="di-card">
        {header}
        <ScanAnimation entry={entry} />
        <div className="mt-3" aria-live="polite">
          <div className="small font-weight-bold text-primary">
            {uploading ? `Subiendo ${entry.fileName}…` : message}
          </div>
          <div className={`di-progress mt-2 ${uploading ? "" : "di-progress-indeterminate"}`}>
            <div className="di-progress-bar" style={uploading ? { width: `${entry.progress || 0}%` } : undefined} />
          </div>
          {!uploading && (
            <div className="small text-muted mt-1">Nuestra inteligencia artificial está analizando tu documento</div>
          )}
        </div>
      </div>
    );
  }

  if (entry && entry.phase === "done") {
    const readable = hasReadableData(entry.analysis);
    const warning = getAnalysisWarning(entry.analysis);
    const recognized = countRecognizedFields(entry.analysis);
    return (
      <div className={`di-card ${readable ? "di-done" : ""}`} aria-live="polite">
        {header}
        <div className={readable ? "text-success font-weight-bold" : "text-warning font-weight-bold"}>
          <i className={`fa ${readable ? "fa-check-circle" : "fa-exclamation-triangle"} mr-2`} />
          {readable
            ? `${recognized} ${recognized === 1 ? "dato reconocido" : "datos reconocidos"}`
            : "Se guardó, pero no se pudo leer"}
        </div>
        <div className="small text-muted text-truncate">{entry.fileName}</div>
        {!readable && entry.analysis && entry.analysis.error && (
          <div className="small text-warning mt-1">{entry.analysis.error}</div>
        )}
        {warning && <div className="small text-warning mt-1">⚠ {warning}. Se revisará.</div>}
        <Button variant="link" size="sm" className="px-0 mt-1" onClick={openPicker}>
          Cambiar archivo
        </Button>
        {hiddenInput}
      </div>
    );
  }

  const dropZone = (
    <div
      className={`di-drop ${dragging ? "di-dragging" : ""}`}
      role="button"
      tabIndex={0}
      aria-label={`Subir ${requirement.label}`}
      onClick={openPicker}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openPicker();
        }
      }}
      onDragEnter={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      <i className="fa fa-cloud-upload-alt di-drop-icon" />
      <div className="font-weight-bold">Arrastra el archivo aquí</div>
      <div className="small text-muted">o haz clic para elegirlo</div>
      {type && (
        <div className="small text-muted mt-1">
          {getAcceptedExtensions(type.mimeTypes)
            .filter((ext) => ext !== ".jpeg")
            .map((ext) => ext.slice(1).toUpperCase())
            .join(" · ")}{" "}
          · máx. {type.maxMb} MB
        </div>
      )}
    </div>
  );

  return (
    <div className={`di-card ${entry && entry.phase === "error" ? "di-error" : ""}`}>
      {header}
      {dropZone}
      {entry && entry.phase === "error" && (
        <div className="small text-danger mt-2" role="alert">
          <i className="fa fa-times-circle mr-1" />
          {entry.error}
        </div>
      )}
      {hiddenInput}
    </div>
  );
};

export default DocumentSlot;
